import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import Collapse from '@material-ui/core/Collapse';
import {SendOutlined, VisibilityOffOutlined, VisibilityOutlined} from '@material-ui/icons';
import Message from '../../../shared/message/Message.js';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import OutlinedButton from '../../../shared/buttons/OutlinedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import {useToast} from '../../../shared/toast/ToastProvider.tsx';
import PreviaEmail from '../../components/PreviaEmail.jsx';
import useCarregamentoPagina from '../../components/useCarregamentoPagina.js';
import useFetch from '../../../../hooks/useFetch.js';
import useCreate from '../../../../hooks/useCreate.js';
import {
    URL_AVALIACAO_DOCUMENTOS_EMAIL_PENDENCIAS,
    URL_AVALIACAO_DOCUMENTOS_EMAIL_PENDENCIAS_PREVIA
} from '../../../../constants/index.js';

const PARAMS = {};
const ID_PREVIA = 'dialog-email-pendencias-previa';

const useStyles = makeStyles(theme => ({
    root: {
        width: 800,
        maxWidth: '100%'
    },
    contentRoot: {overflowY: 'initial'},
    pergunta: {
        marginBottom: theme.spacing(2)
    },
    previa: {
        paddingBottom: theme.spacing(1)
    },
    espaco: {
        flex: 1
    }
}));

const preenchido = valor => !!valor && String(valor).trim() !== '';

/* Motivo de o e-mail não poder ser enviado (o back também recusa), ou null. */
const motivoSemEnvio = previa => {
    if (!previa) {
        return null;
    }
    if (!preenchido(previa.remetente)) {
        return 'O processo de seleção não tem e-mail remetente cadastrado. Cadastre-o no Processo de Seleção para poder enviar.';
    }
    if (!preenchido(previa.emailDestinatario)) {
        return 'O inscrito não tem e-mail ativo cadastrado. Cadastre um e-mail na inscrição para poder enviar.';
    }
    return null;
};

/* "Nome (email)" do destinatário, só o que existir. */
const textoDestinatario = previa => {
    if (!previa) {
        return 'ao inscrito';
    }
    const nome = preenchido(previa.nomeDestinatario) ? previa.nomeDestinatario : 'o inscrito';
    return preenchido(previa.emailDestinatario) ? `para ${nome} (${previa.emailDestinatario})` : `para ${nome}`;
};

/*
 * Envio do e-mail de documentos pendentes da avaliação de documentos: confirma
 * o envio e mostra, sob demanda, o e-mail exatamente como será enviado (De,
 * Para, Cópia, Assunto e o texto com a lista de pendências). A prévia é montada
 * pelo back a cada abertura, com as pendências gravadas no momento.
 */
export default function DialogEnviarEmailPendencias(props) {
    const {open, onClose, idInscricao} = props;
    const classes = useStyles();
    const {createToast} = useToast();
    const [buscar, carregando] = useFetch({returnInnerLoadingState: true});
    const [create, enviando] = useCreate({returnInnerLoadingState: true});
    const [previa, setPrevia] = useState(null);
    const [mostrarEmail, setMostrarEmail] = useState(false);
    // Prévia e envio pelo LoadingModal global (sem spinner dentro do diálogo).
    useCarregamentoPagina(carregando || enviando);

    useEffect(() => {
        if (!open) {
            return;
        }

        setPrevia(null);
        setMostrarEmail(false);
        // Sem prévia (ex.: sem pendências) o envio também falharia: mostra o erro e fecha.
        buscar(URL_AVALIACAO_DOCUMENTOS_EMAIL_PENDENCIAS_PREVIA(idInscricao), PARAMS, setPrevia, (erro, tratarErro) => {
            tratarErro(erro);
            onClose();
        });
    }, [open, idInscricao]);

    const aviso = motivoSemEnvio(previa);

    const fechar = () => {
        if (!enviando) {
            onClose();
        }
    };

    const enviar = () => create(URL_AVALIACAO_DOCUMENTOS_EMAIL_PENDENCIAS(idInscricao), {}, () => {
        createToast({severity: 'success', message: 'E-mail de pendências enviado com sucesso!'});
        onClose();
    });

    return (
        <Dialog
            open={open}
            onClose={fechar}
            maxWidth={false}
            scroll='body'
            classes={{paper: classes.root}}
            aria-labelledby='dialog-email-pendencias-titulo'>
            <DialogTitle id='dialog-email-pendencias-titulo'>Enviar E-mail de Pendências</DialogTitle>
            <DialogContent classes={{root: classes.contentRoot}}>
                <Typography variant='body1' className={classes.pergunta}>
                    {`Tem certeza que deseja enviar o e-mail de pendências de documentos ${textoDestinatario(previa)}? `}
                    Use &quot;Visualizar E-mail&quot; para conferir o texto exatamente como será enviado.
                </Typography>
                {aviso && <Message type='warning' marginBottom>{aviso}</Message>}
                <Collapse in={mostrarEmail && !!previa} id={ID_PREVIA}>
                    {previa && (
                        <div className={classes.previa}>
                            <PreviaEmail
                                remetente={previa.remetente}
                                nomeDestinatario={previa.nomeDestinatario}
                                emailDestinatario={previa.emailDestinatario}
                                assunto={previa.assunto}
                                corpo={previa.corpo}/>
                        </div>
                    )}
                </Collapse>
            </DialogContent>
            <DialogActions>
                <OutlinedButton
                    startIcon={mostrarEmail ? <VisibilityOffOutlined/> : <VisibilityOutlined/>}
                    disabled={!previa}
                    aria-expanded={mostrarEmail}
                    aria-controls={ID_PREVIA}
                    onClick={() => setMostrarEmail(aberto => !aberto)}>
                    {mostrarEmail ? 'Ocultar E-mail' : 'Visualizar E-mail'}
                </OutlinedButton>
                <div className={classes.espaco}/>
                <TextButton onClick={fechar} disabled={enviando}>Cancelar</TextButton>
                <ContainedButton
                    startIcon={<SendOutlined/>}
                    disabled={!previa || !!aviso || carregando || enviando}
                    onClick={enviar}>
                    Confirmar Envio
                </ContainedButton>
            </DialogActions>
        </Dialog>
    );
}

DialogEnviarEmailPendencias.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    /* Id da inscrição avaliada. */
    idInscricao: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};
